import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type ViewEventData } from '@databases/analytics/src/types';
import { getTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  getViewById,
  ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { isEqualOmitUndefined } from '@databases/utils/utils';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../i18n';
import { handleSaveView } from '../utils/utils';

import useView from './useView';

type UpdateViewOptions = {
  analyticsSource: ViewEventData['source'];
  newViewName?: string;
};

type UseUpdateViewOptions = {
  contentId: string;
};

export const useUpdateView = ({ contentId }: UseUpdateViewOptions) => {
  const { view, getViewChangeList, resetViewChangeList } = useView();

  const { formatMessage } = useIntl();

  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const savedViews = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    return database.views;
  });

  const originalView = useMemo(
    () => getViewById(savedViews, view.id),
    [savedViews, view.id]
  );

  return useCallback(
    async ({ analyticsSource, newViewName }: UpdateViewOptions) => {
      const sortUpdated = !isEqualOmitUndefined(
        view.settings.sort,
        originalView?.settings.sort
      );
      const filterUpdated = !isEqualOmitUndefined(
        view.settings.filter,
        originalView?.settings.filter
      );
      const hideFieldsUpdated = !isEqualOmitUndefined(
        view.settings.fields,
        originalView?.settings.fields
      );

      const viewChangeList = getViewChangeList();

      const renamed = newViewName ? newViewName !== originalView?.name : false;

      if (newViewName && renamed) {
        const delta: ViewChangeType = {
          type: ViewChangeTypes.VIEW_NAME,
          value: newViewName,
          delta: getTextDelta(view.name, newViewName),
        };

        await dispatch(
          handleSaveView({
            contentId,
            view: { ...view, name: newViewName },
            viewChangeList: [...viewChangeList, delta],
          })
        );
      } else {
        await dispatch(
          handleSaveView({
            contentId,
            view,
            viewChangeList,
          })
        );
      }

      resetViewChangeList(view.id);

      client.sendViewUpdatedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: analyticsSource,
        viewId: view.id,
        selectedLayout: view.settings.layout.type,
        sortUpdated,
        filterUpdated,
        hideFieldsUpdated,
        renamed,
      });
      currentSnackbarState.setMessage(
        formatMessage(messages['view-settings-panel.update-view-flag-body'], {
          viewName: newViewName && renamed ? newViewName : view.name,
        }),
        {
          id: 'view-updated',
          type: 'success',
          hideAfter: 3000,
          showCloseButton: true,
        }
      );
    },
    [
      view,
      originalView?.settings.sort,
      originalView?.settings.filter,
      originalView?.settings.fields,
      originalView?.name,
      getViewChangeList,
      resetViewChangeList,
      client,
      contentId,
      formatMessage,
      dispatch,
    ]
  );
};
